import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross2Icon,
  DownloadIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import {
  classroomNoteDate,
  classroomNoteFilename,
  classroomNotePlainText,
  type NoteSection,
} from '../domain/classroom-note-share';
import {
  layoutNoteImages,
  loadNoteArtwork,
  noteCanvasMeasure,
  noteCanvasPng,
  renderNoteImage,
  saveNoteImage,
  type NoteImagePage,
  type NoteImageArtwork,
} from '../services/classroom-note-image';
import './classroom-note-share.css';
import { organizeClassroomNote } from '../domain/classroom-study-note';
import { ClassroomStudyNote } from './ClassroomStudyNote';

export function ClassroomNoteShareDialog({
  sections,
  author,
  date,
  onClose,
}: {
  readonly sections: readonly NoteSection[];
  readonly author: string;
  readonly date?: string;
  readonly onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const note = useMemo(() => organizeClassroomNote(sections), [sections]);
  const [title, setTitle] = useState(note.title);
  const effectiveTitle = title.trim() || note.title;
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState<readonly NoteImagePage[]>([]);
  const [exportPreview, setExportPreview] = useState<string | null>(null);
  const [artwork, setArtwork] = useState<NoteImageArtwork | null>(null);
  const [rendered, setRendered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setRendered(false);
    void Promise.all([loadNoteArtwork(), document.fonts.ready])
      .then(([image]) => {
        if (cancelled) return;
        setArtwork(image);
      })
      .catch((reason: unknown) => {
        if (!cancelled) setError(userErrorMessage(reason, '便签生成失败，请重试。'));
      });
    return () => {
      cancelled = true;
    };
  }, [retry]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !artwork) return;
    try {
      const context = canvas.getContext('2d');
      if (!context) throw new Error('当前浏览器不支持生成便签图片。');
      const metadata = { title: effectiveTitle, author, ...(date ? { date } : {}) };
      const layout = layoutNoteImages(note.sections, noteCanvasMeasure(context), metadata);
      if (!layout.length) throw new Error('请先填写课堂笔记，再生成分享便签。');
      const selectedPage = Math.min(page, layout.length - 1);
      setPage(selectedPage);
      setPages(layout);
      renderNoteImage(
        canvas,
        layout[selectedPage]!,
        metadata,
        artwork,
        selectedPage + 1,
        layout.length,
      );
      setExportPreview(layout.length > 1 ? canvas.toDataURL('image/png') : null);
      setError(null);
      setRendered(true);
      setSaved(false);
    } catch (reason: unknown) {
      setRendered(false);
      setError(userErrorMessage(reason, '便签生成失败，请重试。'));
    }
  }, [note, page, artwork, effectiveTitle, author, date]);

  const save = async () => {
    const canvas = canvasRef.current;
    if (!canvas || saving || !rendered || !artwork || pages.length === 0) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const blob = await noteCanvasPng(canvas);
      saveNoteImage(
        blob,
        classroomNoteFilename(effectiveTitle, classroomNoteDate(new Date()), page + 1),
      );
      setSaved(true);
    } catch (reason: unknown) {
      setError(userErrorMessage(reason, '保存失败，请重试。'));
    } finally {
      setSaving(false);
    }
  };
  const ready = rendered && artwork !== null && pages.length > 0;
  return (
    <dialog
      ref={dialogRef}
      className="note-share-dialog"
      aria-labelledby="note-share-heading"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <header className="note-share-dialog__heading">
        <div>
          <h2 id="note-share-heading">分享课堂笔记</h2>
          <p>围棋课堂学习便签 · 保存后即可分享</p>
        </div>
        <button
          type="button"
          className="note-share-close"
          aria-label="关闭分享预览"
          onClick={onClose}
        >
          <Cross2Icon />
        </button>
      </header>
      <div className="note-share-dialog__body">
        <section className="note-share-preview" aria-label="学习便签预览">
          <ClassroomStudyNote
            note={note}
            title={effectiveTitle}
            author={author}
            {...(date ? { date } : {})}
          />
          <canvas ref={canvasRef} hidden aria-hidden="true" />
          <details className="note-share-accessible-text">
            <summary>查看笔记原文</summary>
            <p>{classroomNotePlainText(sections)}</p>
          </details>
        </section>
        <aside className="note-share-settings">
          <label htmlFor="note-share-title">便签标题</label>
          <input
            id="note-share-title"
            value={title}
            maxLength={120}
            disabled={saving}
            onChange={(event) => setTitle(event.currentTarget.value)}
          />
          <p>按原笔记分组，统一阅读字号与配色。内容和顺序保留，不改动课堂笔记原文。</p>
          {pages.length > 1 ? (
            <p>长笔记将导出为 {pages.length} 张图片，不删减内容。下方可预览并逐张保存。</p>
          ) : null}
          {pages.length > 1 ? (
            <nav className="note-share-pages" aria-label="便签分页">
              <button
                type="button"
                aria-label="上一张便签"
                disabled={page === 0 || saving}
                onClick={() => setPage((value) => value - 1)}
              >
                <ChevronLeftIcon />
              </button>
              <span aria-live="polite">
                图片 {page + 1} / {pages.length}
              </span>
              <button
                type="button"
                aria-label="下一张便签"
                disabled={page === pages.length - 1 || saving}
                onClick={() => setPage((value) => value + 1)}
              >
                <ChevronRightIcon />
              </button>
            </nav>
          ) : null}
          {ready && exportPreview ? (
            <img
              className="note-share-export-thumbnail"
              src={exportPreview}
              alt={`导出图片第 ${page + 1} 张预览`}
            />
          ) : null}
          <button
            type="button"
            className="note-share-save"
            disabled={!ready || saving}
            onClick={() => void save()}
          >
            <DownloadIcon />
            {saving ? '正在保存…' : pages.length > 1 ? '保存当前图片' : '保存图片'}
          </button>
          <small>图片仅在当前设备生成，不上传笔记。iPad / iPhone 可在“下载”中查看。</small>
          {!ready && !error ? <p role="status">正在准备导出图片…</p> : null}
          {saved ? (
            <p className="note-share-success" role="status">
              已发起下载{pages.length > 1 ? '，切换下一张可继续保存' : ''}。
            </p>
          ) : null}
          {error ? (
            <div className="note-share-error" role="alert">
              <p>{error}</p>
              <button
                type="button"
                onClick={() => {
                  setArtwork(null);
                  setRetry((value) => value + 1);
                }}
              >
                <ReloadIcon />
                重新生成
              </button>
            </div>
          ) : null}
        </aside>
      </div>
    </dialog>
  );
}
